import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Search, CheckCircle, Clock, XCircle } from 'lucide-react';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import { formatDate } from '../../lib/utils';

export default function KOLSubmissions() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const response = await api.get('/submissions');
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const matchesSearch =
      sub.invitation?.job?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.invitation?.kolProfile?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const needsAction = submissions.filter(s => s.status === 'REVISION_REQUESTED');

  const statusOptions = [
    { value: 'ALL', label: 'All Status' },
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REVISION_REQUESTED', label: 'Revision Requested' },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SUBMITTED': return <Badge variant="default">Submitted</Badge>;
      case 'APPROVED': return <Badge variant="success">Approved</Badge>;
      case 'REVISION_REQUESTED': return <Badge variant="destructive">Revision Requested</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'REVISION_REQUESTED': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'SUBMITTED': return <Clock className="w-5 h-5 text-blue-600" />;
      default: return <Video className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">My Submissions</h1>
        <p className="text-glass-text-2 text-sm mt-1">Track your content submissions</p>
      </div>

      {/* Action Alert */}
      {needsAction.length > 0 && (
        <Card className="border-[rgb(var(--warn)/0.35)] bg-[rgb(var(--warn)/0.1)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">
                  {needsAction.length} submission{needsAction.length !== 1 ? 's need' : ' needs'} revision
                </p>
                <p className="text-sm text-orange-700">
                  Please review the feedback and resubmit from your invitation page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {submissions.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by campaign..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submissions List */}
      <Card>
        <CardHeader>
          <CardTitle>
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              {`${filteredSubmissions.length} Submission${filteredSubmissions.length !== 1 ? 's' : ''}`}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading...</p>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No submissions yet</p>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                You'll be able to submit content once the agency approves you for production on a campaign. Check your <a href="/kol/invitations" className="text-green-600 hover:underline">Invitations</a> page for pending opportunities.
              </p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No submissions match your filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredSubmissions.map((sub) => (
                <div
                  key={sub.id}
                  onClick={() => navigate(`/kol/submissions/${toSlug(sub.invitation?.job?.title || 'submission', sub.id)}`)}
                  className="flex items-center justify-between p-4 border border-glass rounded-lg hover:bg-glass-3/40 cursor-pointer transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(sub.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {sub.invitation?.job?.title || 'Untitled Campaign'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Version {sub.version} • {sub.media?.length || 0} media file{(sub.media?.length || 0) !== 1 ? 's' : ''}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Submitted {formatDate(sub.createdAt)}
                        </p>
                        {sub.revisionComments && sub.status === 'REVISION_REQUESTED' && (
                          <p className="text-sm text-red-700 mt-2 p-2 bg-red-50 rounded border-l-2 border-red-500">
                            {sub.revisionComments}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(sub.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
